/**
 * The address cell of a route stop: shows the location, and — when the row is
 * editable — swaps into the autocomplete so a dispatcher can replace it.
 * Lifted out of `RouteStopView` verbatim; it was already a component, just
 * co-located with a file that had grown past a thousand lines.
 */
import React, { useCallback, useContext, useMemo, useState } from "react"
import { Button, MenuItem, Popover } from "@blueprintjs/core"
import { useMediaQuery } from "@react-hookz/web"
import { ChatHistoryContext } from "@/contexts/ChatHistoryContext"
import { Location } from "@/reducers/locationsSlice"
import { RouteStop } from "@/reducers/routes2gSlice"
import { AutocompleteGooglePlaces } from "@/components/maps/AutocompleteGooglePlaces"
import RouteStopDetailsPopover from "@/components/RouteStopDetailPopover"
import { HoverPopover } from "@/components/ui/hover-popover"
import { Button as Button2 } from "@/components/ui/button"
import css from "@/components/RouteStopView.module.css"
import { isMobile } from "@/utils/detectDevice"
import { Trans, useTranslation } from "react-i18next"

// gbox event status → translation key; the label is translated at render.
const gboxStatusLabelKeys = {
  0: "maps.locationView.gboxStatus.new",
  1: "maps.locationView.gboxStatus.received",
  2: "maps.locationView.gboxStatus.orderAccepted",
  3: "maps.locationView.gboxStatus.approach",
  4: "maps.locationView.gboxStatus.waiting",
  5: "maps.locationView.gboxStatus.inProgress",
  6: "maps.locationView.gboxStatus.finished",
  7: "maps.locationView.gboxStatus.started",
  9: "maps.locationView.gboxStatus.execution",
} as const

const gboxStatusColors: Record<number, string> = {
  0: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  1: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  2: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  3: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
  4: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
  5: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  6: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
  7: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  9: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
}

export type LocationEditViewProps = {
  location?: Location
  onUpdate: (location: Location) => void
  onAbort: () => void
}

export const LocationEditView = ({
  location,
  onUpdate,
  onAbort,
}: LocationEditViewProps) => {
  const { t } = useTranslation()
  const handleLocationSelect = useCallback(
    (selectedLocation: Location) => {
      onUpdate(selectedLocation)
      onAbort() // Close edit mode after selection
    },
    [onUpdate, onAbort],
  )

  return (
    <AutocompleteGooglePlaces
      initialValue={location?.formattedAddress || ""}
      placeholder={t("maps.locationView.enterAddress")}
      autoFocus
      onLocationSelect={handleLocationSelect}
      onAbort={onAbort}
    />
  )
}

type LocationViewProps = {
  stop: RouteStop
  disabled: boolean
  detected: boolean
  onUpdate: (location: Location) => void
  isEditing?: boolean
  onEditStart?: () => void
  onEditEnd?: () => void
  isReadOnlyMap?: boolean
}
const LocationView = ({
  stop,
  disabled,
  detected,
  onUpdate,
  isEditing = false,
  onEditStart,
  onEditEnd,
  isReadOnlyMap = false,
}: LocationViewProps) => {
  const { t } = useTranslation()
  const { location } = stop
  const sourceMessageId = stop.msgId

  const { jumpAndHighlightMessage } = useContext(ChatHistoryContext)

  const getCoordinates = (location: Location) => {
    if (location.lat && location.lon) {
      const lat = parseFloat(location.lat).toFixed(5)
      const lon = parseFloat(location.lon).toFixed(5)
      return `${lat}, ${lon}`
    }
    return undefined
  }

  const getLocationAddress = useCallback((location: Location) => {
    if (location.formattedAddress?.trim()) {
      return location.formattedAddress.trim()
    }

    if (location.countryCode && location.locality) {
      return `${location.countryCode}, ${location.postalCode ? location.postalCode : ""} ${location.locality}`
    }

    return getCoordinates(location)
  }, [])

  const locationLabel = useMemo(() => {
    const companyName = location.companyName?.trim()
    const address = getLocationAddress(location)

    if (companyName) {
      return {
        primary: companyName,
        secondary:
          address && address !== companyName && address.trim().length > 0
            ? address
            : undefined,
      }
    }

    return {
      primary: address || "?",
      secondary: undefined,
    }
  }, [getLocationAddress, location])

  const handleClick = () => {
    if (disabled || isReadOnlyMap) return

    onEditStart?.()
  }

  const onEditAbort = useCallback(() => {
    onEditEnd?.()
  }, [onEditEnd])

  const AiDetectedTag = () => (
    <span className="cursor-pointer text-xs text-gray-400">
      <a
        onClick={(e: React.MouseEvent<HTMLElement>) => {
          e.stopPropagation()
          if (sourceMessageId) {
            jumpAndHighlightMessage(sourceMessageId)
          }
        }}
        className="dark:text-gray-200 dark:hover:text-white"
      >
        <Trans
          i18nKey="maps.locationView.addedBy"
          components={{ ai: <span className={css.slickshiftAI} /> }}
        />
      </a>
    </span>
  )

  const isSmallScreen = useMediaQuery("(max-width: 900px)")

  return (
    <div
      className="w-full"
      onClick={!isMobile && !isSmallScreen ? handleClick : undefined}
    >
      {isEditing ? (
        <LocationEditView
          location={location}
          onUpdate={onUpdate}
          onAbort={onEditAbort}
        />
      ) : stop.is_temporary ? (
        <Button
          className="w-full justify-start dark:text-gray-200 dark:hover:bg-gray-700"
          minimal
          disabled={disabled}
        >
          <div className="flex min-w-0 flex-col text-left">
            <div className="truncate">{locationLabel.primary}</div>
            {locationLabel.secondary && (
              <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                {locationLabel.secondary}
              </div>
            )}
          </div>
        </Button>
      ) : (
        <HoverPopover
          disabled={isMobile || isEditing}
          content={<RouteStopDetailsPopover routeStop={stop} />}
          side="bottom"
        >
          <Button
            className="w-full justify-start dark:text-gray-200 dark:hover:bg-gray-700"
            minimal
            disabled={disabled}
          >
            <div className="flex min-w-0 flex-col text-left">
              <div className="truncate">{locationLabel.primary}</div>
              {locationLabel.secondary && (
                <div className="truncate text-xs text-gray-500 dark:text-gray-400">
                  {locationLabel.secondary}
                </div>
              )}
              {detected && <AiDetectedTag />}
              {stop.source_metadata?.event_status !== undefined &&
                (() => {
                  const status = stop.source_metadata.event_status as number
                  const labelKey =
                    gboxStatusLabelKeys[
                      status as keyof typeof gboxStatusLabelKeys
                    ]
                  return (
                    <div className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500">
                      {t("maps.locationView.gboxStatusLabel")}
                      <span
                        className={`inline-block rounded px-1.5 py-0.5 font-medium ${gboxStatusColors[status] ?? "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"}`}
                      >
                        {status} - {labelKey ? t(labelKey) : "?"}
                      </span>
                    </div>
                  )
                })()}
            </div>
          </Button>
        </HoverPopover>
      )}
    </div>
  )
}

export default LocationView
